import { forwardRef, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

/* ─── Timing constants ─── */
const FAST = 0.2;
const MEDIUM = 0.25;
const EASE = [0.25, 0.1, 0.25, 1]; // cubic-bezier, smooth & premium

/* ─── MotionCard ─── */
/* Adds hover lift + shadow. Wraps any card with subtle y-translate & box-shadow on hover. */
export const MotionCard = forwardRef(function MotionCard(
  { children, className = "", delay = 0, ...rest },
  ref
) {
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MEDIUM, delay, ease: EASE }}
      whileHover={{
        y: -3,
        boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,184,212,0.08)",
        transition: { duration: FAST, ease: EASE },
      }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
});

/* ─── MotionCardLink ─── */
/* Same as MotionCard but renders a react-router <Link>. */
const MotionLink = motion.create(Link);

export const MotionCardLink = forwardRef(function MotionCardLink(
  { children, to, className = "", delay = 0, ...rest },
  ref
) {
  return (
    <MotionLink
      ref={ref}
      to={to}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MEDIUM, delay, ease: EASE }}
      whileHover={{
        y: -3,
        boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,184,212,0.08)",
        transition: { duration: FAST, ease: EASE },
      }}
      className={className}
      {...rest}
    >
      {children}
    </MotionLink>
  );
});

/* ─── AnimatedButton ─── */
/* Scale tap feedback + subtle ripple. */
export function AnimatedButton({
  children,
  className = "",
  onClick,
  disabled,
  type = "button",
  ...rest
}) {
  const btnRef = useRef(null);

  const handleClick = useCallback(
    (e) => {
      if (disabled) return;

      // Ripple effect
      const btn = btnRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const ripple = document.createElement("span");
        ripple.className = "motion-ripple";
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        btn.appendChild(ripple);
        setTimeout(() => ripple.remove(), 500);
      }

      onClick?.(e);
    },
    [onClick, disabled]
  );

  return (
    <motion.button
      ref={btnRef}
      type={type}
      disabled={disabled}
      onClick={handleClick}
      whileTap={{ scale: disabled ? 1 : 0.97 }}
      whileHover={{ scale: disabled ? 1 : 1.01 }}
      transition={{ duration: 0.15, ease: EASE }}
      className={`relative overflow-hidden ${className}`}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

/* ─── PageTransition ─── */
/* Fade + slight slide-up for page content. Wrap each page's root element. */
export function PageTransition({ children, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: MEDIUM, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* Re-export AnimatePresence for convenience */
export { AnimatePresence };

/* ─── Skeleton ─── */
/* Pulsing placeholder block for loading states. */
export function Skeleton({ className = "", rounded = "rounded-2xl" }) {
  return (
    <motion.div
      className={`bg-[color:var(--surface-soft)] ${rounded} ${className}`}
      initial={{ opacity: 0.4 }}
      animate={{ opacity: [0.4, 0.7, 0.4] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}

/* Skeleton presets for common shapes */
export function CardSkeleton() {
  return (
    <div className="glass rounded-2xl p-5 shadow-[var(--shadow-card)] space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 shrink-0" rounded="rounded-md" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" rounded="rounded-md" />
          <Skeleton className="h-3 w-1/2" rounded="rounded-md" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" rounded="rounded-md" />
    </div>
  );
}

export function StatSkeleton() {
  return (
    <div className="glass rounded-2xl p-4 shadow-[var(--shadow-card)] space-y-2">
      <Skeleton className="h-3 w-20" rounded="rounded-md" />
      <Skeleton className="h-7 w-16" rounded="rounded-md" />
    </div>
  );
}

export function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <Skeleton className="h-4 w-24" rounded="rounded-md" />
      <Skeleton className="h-4 w-20" rounded="rounded-md" />
      <Skeleton className="h-4 flex-1" rounded="rounded-md" />
      <Skeleton className="h-4 w-16" rounded="rounded-md" />
      <Skeleton className="h-8 w-20" rounded="rounded-lg" />
    </div>
  );
}

/* ─── StaggerContainer ─── */
/* Staggers children on mount. Wrap a list of MotionCards. */
export function StaggerContainer({ children, className = "", stagger = 0.05 }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* Variant presets for stagger children */
export const staggerChild = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: MEDIUM, ease: EASE },
  },
};

/* ─── StepProgress ─── */
/* Animated progress bar + step indicators for booking flow. */
export function StepProgress({ steps, currentStep }) {
  const progress = currentStep / (steps.length - 1);
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const done = i < currentStep;
        const active = i === currentStep;
        return (
          <div key={s.label} className="flex items-center gap-2 flex-shrink-0">
            <motion.div
              initial={false}
              animate={{
                scale: active ? 1 : 0.95,
                opacity: active || done ? 1 : 0.5,
              }}
              transition={{ duration: FAST, ease: EASE }}
              className="flex items-center gap-2"
            >
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  active
                    ? isDark
                      ? "bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF]"
                      : "bg-brand-blue/10 border border-brand-blue/30 text-brand-blue"
                    : done
                    ? isDark ? "text-white/60 cursor-pointer" : "text-slate-600 cursor-pointer"
                    : "text-[#8F9098] cursor-default"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center ${
                    done
                      ? isDark ? "bg-[#00F0FF]" : "bg-brand-blue"
                      : active
                      ? isDark ? "border border-[#00F0FF]" : "border border-brand-blue"
                      : isDark ? "border border-[#222226]" : "border border-slate-300"
                  }`}
                >
                  {done ? (
                    <motion.svg
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="black"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <motion.polyline
                        points="20 6 9 17 4 12"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: EASE }}
                      />
                    </motion.svg>
                  ) : (
                    <span className="text-[8px] font-bold">{i + 1}</span>
                  )}
                </div>
                {s.label}
              </div>
            </motion.div>
            {i < steps.length - 1 && (
              <div className={`w-4 h-0.5 rounded-full overflow-hidden ${isDark ? "bg-[#222226]" : "bg-slate-200"}`}>
                <motion.div
                  className={`h-full ${isDark ? "bg-[#00F0FF]" : "bg-brand-blue"}`}
                  initial={{ width: "0%" }}
                  animate={{
                    width: i < currentStep ? "100%" : "0%",
                  }}
                  transition={{ duration: 0.4, ease: EASE }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
